import { Injectable } from '@angular/core';
import { DataService } from 'src/app/services/data.service';
import {
  PRIZE_WEIGHT_SECTIONS,
  PrizeWeightFiltersStateService,
  PrizeWeightRow,
  PrizeWeightSectionKey,
} from './prize-weight-filters-state.service';
import { DateFilterRow, PrizeDateFilterStateService } from './prize-date-filter-state.service';
import { PrizeTagFilterStateService, TagFilterState } from './prize-tag-filter-state.service';

/** Backend route for PrizeFilterVersionController — see api/src/entities/prize-filter-version/. */
const ENTITY_TYPE = 'prize-filter-versions';

/**
 * True if a save/load failed because the user's role lacks a PrizeFilterVersionController
 * permission (HTTP 403). Retrying can't fix that, so callers show a permission message instead of
 * the generic "please try again" one (CMS-6336: ROLE_STAFF had no PrizeFilterVersion permissions).
 */
export function isPermissionDenied(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === 403;
}

/** Toast detail for a 403 on save. */
export const SAVE_FILTERS_DENIED_DETAIL =
  "Your role doesn't have permission to save the shared default filters. Ask an admin to grant Prize Filter Versions access.";

/** Toast detail for a 403 on load. */
export const LOAD_FILTERS_DENIED_DETAIL =
  "Your role doesn't have permission to read the saved filters, so each filter section starts from its built-in defaults. Ask an admin to grant Prize Filter Versions access.";

export interface PrizeFilterPayload {
  weightSections: Record<PrizeWeightSectionKey, PrizeWeightRow[]>;
  dateRows: DateFilterRow[];
  tagState: TagFilterState;
}

/**
 * Persists the Generate panel's Filters section (weight tables, date rules, tag rules) as a
 * single, globally shared configuration — not per-operator. Every save inserts a brand-new
 * `prize-filter-versions` document (see PrizeFilterVersionController, an append-only entity); the
 * most recently saved one is always the active default. There's no UI yet to browse or revert to
 * older versions, but since each save is its own row (not an overwrite), that's an additive
 * change to build later rather than a schema migration.
 */
@Injectable({
  providedIn: 'root',
})
export class PrizeFilterDefaultsService {
  /** Memoizes the in-flight/completed loadLatest() call so multiple surfaces (Generate panel, Filters drawer) awaiting it concurrently share one fetch and one applyPayload(), rather than racing duplicate loads. */
  private loadPromise: Promise<boolean> | null = null;

  constructor(
    private dataService: DataService,
    private weightFiltersState: PrizeWeightFiltersStateService,
    private dateFilterState: PrizeDateFilterStateService,
    private tagFilterState: PrizeTagFilterStateService,
  ) {}

  buildCurrentPayload(): PrizeFilterPayload {
    const weightSections = {} as Record<PrizeWeightSectionKey, PrizeWeightRow[]>;
    PRIZE_WEIGHT_SECTIONS.forEach((section) => {
      weightSections[section.key] = this.weightFiltersState.getRows(section.key);
    });

    return {
      weightSections,
      dateRows: this.dateFilterState.getRows(),
      tagState: this.tagFilterState.getState(),
    };
  }

  applyPayload(payload: PrizeFilterPayload): void {
    PRIZE_WEIGHT_SECTIONS.forEach((section) => {
      const rows = payload.weightSections?.[section.key];
      if (rows) {
        this.weightFiltersState.setRows(section.key, rows);
      }
    });
    if (payload.dateRows) {
      this.dateFilterState.setRows(payload.dateRows);
    }
    if (payload.tagState) {
      this.tagFilterState.setState(payload.tagState);
    }
  }

  /** Saves the current in-memory filter state as a new active global default. Callers must run the submit guards first — this performs no validation of its own. */
  async saveCurrentAsNewVersion(): Promise<void> {
    const filterPayload = this.buildCurrentPayload();
    await this.dataService.addNewRecord(ENTITY_TYPE, { filterPayload, enabled: true });
  }

  /**
   * Loads the most recently saved version into the filter state services. Returns false if
   * nothing has ever been saved (every session then starts from each service's own
   * product-specified defaults). Safe to call from more than one surface (e.g. the Generate panel
   * and the Filters drawer both call this on init) — the underlying fetch/apply only happens once;
   * later callers await the same in-flight or already-settled result.
   */
  async loadLatest(): Promise<boolean> {
    if (!this.loadPromise) {
      this.loadPromise = this.fetchAndApplyLatest();
    }
    return this.loadPromise;
  }

  private async fetchAndApplyLatest(): Promise<boolean> {
    const latest = await this.dataService.getDocumentAsync(ENTITY_TYPE, {
      query: {},
      sort: { createdAt: -1 },
    });
    if (!latest || !latest.filterPayload) {
      return false;
    }
    this.applyPayload(latest.filterPayload);
    return true;
  }
}
